import { createAsyncThunk } from "@reduxjs/toolkit";
import { setAuthToken } from "../../utils/tokenHeaders";
import { userSlice } from "../user/userSlice";

const prodURL = "/api";
const url =
  window.location.hostname === "localhost"
    ? "http://localhost:3004/api"
    : prodURL;

async function handleResponse(response) {
  if (response.status === 401) {
    localStorage.removeItem("userData");
    localStorage.removeItem("token");
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Request failed");
  }

  return response;
}

export function fetchWithAuth(url, options = {}) {
  return fetch(`${url}`, {
    ...options,
    headers: {
      ...options.headers,
      "Content-Type": "application/json",
      Authorization: setAuthToken(),
    },
  }).then(handleResponse);
}

export const registrationUser = createAsyncThunk(
  "user/register",
  async function (props) {
    const { login, password, key } = props;

    const response = await fetch(`${url}/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        login,
        password,
        key,
      }),
    });

    const result = await response.text();
    return JSON.parse(result);
  }
);

export const loginUser = createAsyncThunk("user/login", async function (props) {
  const { login, password } = props;

  const response = await fetch(`${url}/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      login,
      password,
    }),
  });

  const result = await response.text();
  const parseResult = JSON.parse(result);

  return parseResult;
});

export const validateToken = createAsyncThunk(
  "user/validate",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${url}/validate`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: setAuthToken(),
        },
      });

      const result = await response.json();

      if (result.valid && result.user) {
        return {
          user: result.user,
        };
      } else {
        return rejectWithValue(result);
      }
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getGamesData = createAsyncThunk(
  "user/getGamesData",
  async function (props, dispatch) {
    const { id } = props;

    const response = await fetchWithAuth(`${url}/me/${id}`, {
      method: "GET",
    });
    const result = await response.text();

    if (result.type === "error") {
      return dispatch(userSlice.addMessage);
    } else {
      return JSON.parse(result);
    }
  }
);

export const getGame = createAsyncThunk(`user/getGame`, async function (props) {
  const { gameId } = props;

  const response = await fetchWithAuth(`${url}/game/${gameId}`, {
    method: "GET",
  });

  const responseData = await response.json();

  if (responseData) {
    return responseData;
  }
});

export const addNewGame = createAsyncThunk(
  "user/addNewGame",
  async function (props) {
    const { userId, textProject, onSuccess } = props;

    const response = await fetch(`${url}/new`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        userId,
        textProject,
      }),
    });

    const result = await response.text();

    onSuccess?.();

    return JSON.parse(result);
  }
);

export const deleteGame = createAsyncThunk(
  "user/deleteGame",
  async function (props) {
    const { userId, id, onSucces } = props;

    const response = await fetch(`${url}/delete/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        userId: userId,
      }),
    });

    const result = await response.text();

    if (result) {
      onSucces?.();

      return JSON.parse(result);
    }
  }
);

export const saveGame = createAsyncThunk(
  "user/saveGame",
  async function (props) {
    const { selectedGameId, nodes, edges } = props;

    const response = await fetch(`${url}/save`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        data: {
          selectedGameId,
          nodes,
          edges,
        },
      }),
    });

    const result = await response.text();
    return JSON.parse(result);
  }
);

export const saveGameSettings = createAsyncThunk(
  "user/saveGameSettings",
  async function (props) {
    const { game, title, description, category, settings, onSucces } = props;

    const response = await fetch(`${url}/project-settings`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        data: {
          id: game,
          title,
          description,
          category,
          settings,
        },
      }),
    });

    onSucces?.();

    const result = await response.text();
    return JSON.parse(result);
  }
);

export const getLinks = createAsyncThunk(
  "user/getLinks",
  async function (props) {
    const { userId } = props;

    const response = await fetchWithAuth(`${url}/links`, {
      method: "POST",
      body: JSON.stringify({
        data: {
          userId,
        },
      }),
    });

    const result = await response.text();
    return JSON.parse(result);
  }
);

export const loadVideoScene = createAsyncThunk(
  `user/addVideoScene`,
  async function (props) {
    const { userId, selectedGameId, scene, file, game } = props;

    const formData = new FormData();

    formData.append("videoFile", file);
    formData.append("gameData", JSON.stringify(game));

    const response = await fetch(
      `${url}/upload/${userId}/${selectedGameId}/${scene}`,
      {
        method: "POST",
        headers: {
          Authorization: setAuthToken(),
        },
        body: formData,
      }
    );

    const result = await response.text();

    return JSON.parse(result);
  }
);

export const loadPoster = createAsyncThunk(
  `user/loadPoster`,
  async function (props) {
    const { gameId, base64String, onSucces } = props;

    const formData = new FormData();
    formData.append("imageFile", base64String);

    const response = await fetch(`${url}/upload/${gameId}`, {
      method: "POST",
      headers: {
        Authorization: setAuthToken(),
      },
      body: formData,
    });

    const result = await response.text();

    if (result) {
      onSucces?.();

      return JSON.parse(result);
    }
  }
);

export const deletePoster = createAsyncThunk(
  "user/deletePoster",
  async function (props) {
    const { gameId, onSucces } = props;

    const response = await fetch(`${url}/deletePoster/${gameId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
    });

    const result = await response.text();

    if (result) {
      onSucces?.();

      return JSON.parse(result);
    }
  }
);

export const addDescription = createAsyncThunk(
  "user/addDescription",
  async function (props) {
    const { selectedGameId, description } = props;

    const response = await fetchWithAuth(`${url}/description`, {
      method: "POST",
      body: JSON.stringify({
        data: {
          selectedGameId,
          description,
        },
      }),
    });

    const result = await response.text();
    return JSON.parse(result);
  }
);

export const generateKey = createAsyncThunk(
  "user/generateKey",
  async function (props) {
    const { game, user } = props;

    const response = await fetch(`${url}/generate-key`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        user,
        game,
      }),
    });

    const result = await response.text();

    return JSON.parse(result);
  }
);

export const getProfile = createAsyncThunk(
  `user/getProfile`,
  async function (props) {
    const { id } = props;

    const response = await fetchWithAuth(`${url}/profile/user/${id}`, {
      method: "GET",
    });

    const result = await response.text();

    return JSON.parse(result);
  }
);

export const confirmEmailUser = createAsyncThunk(
  "user/email",
  async function (props) {
    const { id, email } = props;

    const response = await fetchWithAuth(`${url}/profile/email`, {
      method: "POST",
      body: JSON.stringify({
        id,
        email,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    return parseResult;
  }
);

export const confirmCodeEmail = createAsyncThunk(
  "user/confirmCodeEmail",
  async function (props) {
    const { id, code } = props;

    const response = await fetchWithAuth(`${url}/profile/confirm-email`, {
      method: "POST",
      body: JSON.stringify({
        id,
        code,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    return parseResult;
  }
);

export const confirmTG = createAsyncThunk(
  "user/generate-tg",
  async function (props) {
    const { id, onSucces } = props;

    const response = await fetch(`${url}/profile/generate-tg`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        id,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    onSucces?.();

    return parseResult;
  }
);

export const confirmBirthday = createAsyncThunk(
  "user/birthday",
  async function (props) {
    const { id, date } = props;

    const response = await fetch(`${url}/profile/birthday`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        id,
        date,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    return parseResult;
  }
);

export const createPayment = createAsyncThunk(
  "user/create-payment",
  async function (props) {
    const { id, price } = props;

    const response = await fetch(`${url}/payment/create-payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        id,
        price,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    return parseResult;
  }
);

export const deleteAccaunt = createAsyncThunk(
  "user/delete-accaunt",
  async function (props) {
    const { userId, onSuccess } = props;

    const response = await fetchWithAuth(`${url}/profile/delete-accaunt`, {
      method: "POST",
      body: JSON.stringify({
        userId,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    onSuccess?.();

    return parseResult;
  }
);

export const resetPassword = createAsyncThunk(
  "user/reset",
  async function (props) {
    const { email } = props;

    const response = await fetch(`${url}/reset`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    return parseResult;
  }
);

export const sendNewUserData = createAsyncThunk(
  "user/reset-password",
  async function (props) {
    const { token, pass } = props;

    const response = await fetch(`${url}/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token,
        pass,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    return parseResult;
  }
);

// export const getVideo = createAsyncThunk(
//   `user/getVideo`,
//   async function (props) {
//     const { selectedGameId } = props;

//     const response = await fetch(`${url}/video/${selectedGameId}`, {
//       method: "GET",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: setAuthToken(),
//       },
//     });

//     const result = await response.text();

//     return JSON.parse(result);
//   }
// );

// export const getNotifications = createAsyncThunk(
//   "user/getNotifications",
//   async function (props) {
//     const { userId } = props;

//     const response = await fetch(`${url}/notifications`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({
//         data: {
//           userId,
//         },
//       }),
//     });

//     const result = await response.text();
//     return JSON.parse(result);
//   }
// );

// export const getGamesForModerator = createAsyncThunk(
//   "user/getGamesForModerator",
//   async function (props) {
//     const { id } = props;

//     const response = await fetch(`${url}/moderator/games`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({
//         id,
//       }),
//     });

//     const result = await response.text();
//     const parseResult = JSON.parse(result);

//     return parseResult;
//   }
// );

// export const getUsersForModerator = createAsyncThunk(
//   "user/getUsersForModerator",
//   async function (props) {
//     const { id } = props;

//     const response = await fetch(`${url}/moderator/users`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({
//         id,
//       }),
//     });

//     const result = await response.text();
//     const parseResult = JSON.parse(result);

//     return parseResult;
//   }
// );

// export const postNews = createAsyncThunk(
//   "user/postNews",
//   async function (props) {
//     const { id, description } = props;

//     const response = await fetch(`${url}/moderator/news`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({
//         id,
//         description,
//       }),
//     });

//     const result = await response.text();
//     const parseResult = JSON.parse(result);

//     return parseResult;
//   }
// );
